import { getAssociatedTokenAddressSync } from '@solana/spl-token';
import { type Connection, PublicKey } from '@solana/web3.js';
import type BN from 'bn.js';
import { fetchMarket } from '../accounts/fetch.js';
import { estimateComputeUnits } from '../constants/cu-table.js';
import { MORFI_OPINION_PROGRAM_ID, TOKEN_PROGRAM_ID } from '../constants/programs.js';
import { getProgram } from '../idl/loader.js';
import type { BuildParams, BuildResult } from '../types/build-result.js';
import {
  createBuildResult,
  createIdempotentAtaIx,
  invokeMethod,
  uniqueSignerKeys,
} from './common.js';

const VAULT_AUTHORITY_SEED: Buffer = Buffer.from('market_quote_vault_authority');
const VAULT_SEED: Buffer = Buffer.from('market_quote_vault');
const EVENT_AUTHORITY_SEED: Buffer = Buffer.from('__event_authority');

export interface BoostMarketParams extends BuildParams {
  booster: PublicKey;
  market: PublicKey;
  amount: BN;
  /** Indexed or prehydrated immutable market config binding. */
  marketConfig?: PublicKey;
  /** Indexed or prehydrated quote mint. */
  quoteMint?: PublicKey;
  /** Bound timed schedule, or null for conviction markets. Omit to read the market. */
  eventSchedule?: PublicKey | null;
  /** Rent payer for the booster_quote_ata idempotent-create; defaults to booster. */
  payer?: PublicKey;
}

/**
 * Build a boost_market transaction. Transfers `amount` of quote (USDC, D6) from
 * the booster's quote ATA into the market's shared quote vault, increasing its
 * prize liability while Trading. Supplying marketConfig, quoteMint and
 * eventSchedule avoids the existing market-account read.
 *
 * Wraps IDL instruction `boost_market` — see contracts/markets/idl/markets.json.
 *
 * @param connection - Solana RPC connection.
 * @param params - Boost parameters. See {@link BoostMarketParams}.
 * @returns BuildResult with unsigned transaction ready for signing.
 */
export async function buildBoostMarket(
  connection: Connection,
  params: BoostMarketParams,
): Promise<BuildResult> {
  const programId = params.programId ?? MORFI_OPINION_PROGRAM_ID;
  const program = await getProgram(connection, programId);
  let { marketConfig, quoteMint, eventSchedule } = params;
  if (marketConfig === undefined || quoteMint === undefined || eventSchedule === undefined) {
    const marketAccount = await fetchMarket(connection, params.market, programId);
    marketConfig ??= marketAccount.config;
    quoteMint ??= marketAccount.quoteMint;
    if (eventSchedule === undefined) eventSchedule = marketAccount.eventSchedule;
  }
  const boosterQuoteAta = getAssociatedTokenAddressSync(quoteMint, params.booster);
  const marketQuoteVaultAuthority = deriveMarketPda(VAULT_AUTHORITY_SEED, params.market, programId);
  const marketQuoteVault = deriveMarketPda(VAULT_SEED, params.market, programId);

  const ataPayer = params.payer ?? params.booster;
  const preInstructions = [
    createIdempotentAtaIx(ataPayer, params.booster, quoteMint, TOKEN_PROGRAM_ID),
  ];

  const instruction = await invokeMethod(program, 'boostMarket', params.amount)
    .accounts({
      booster: params.booster,
      market: params.market,
      marketConfig,
      quoteMint,
      boosterQuoteAta,
      marketQuoteVaultAuthority,
      marketQuoteVault,
      tokenProgram: TOKEN_PROGRAM_ID,
      eventSchedule: eventSchedule?.equals(PublicKey.default) ? null : eventSchedule,
      eventAuthority: PublicKey.findProgramAddressSync([EVENT_AUTHORITY_SEED], programId)[0],
      program: programId,
    })
    .instruction();

  const computeUnits = params.computeUnits ?? estimateComputeUnits('boost_market');
  return createBuildResult(
    [...preInstructions, instruction],
    uniqueSignerKeys(params.booster, ataPayer),
    computeUnits,
  );
}

function deriveMarketPda(seed: Buffer, market: PublicKey, programId: PublicKey): PublicKey {
  return PublicKey.findProgramAddressSync([seed, market.toBuffer()], programId)[0];
}
